// DashboardPage — shows live counter cards from GET /api/v1/dashboard
// (rolling-30d window for recovered counts, handled server-side).
// Existing IntegrationsCard / UpgradeCard / Footer remain below the counters.

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, DollarSign, PieChart } from "lucide-react";
import { fetchDashboard, type DashboardCounters } from "../api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@avagency/frontend/components/ui/card";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";
import { IntegrationsCard } from "../components/IntegrationsCard";
import { UpgradeCard } from "../components/UpgradeCard";
import { Footer } from "../components/Footer";

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

interface StatCardProps {
  title: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ReactNode;
  iconClass?: string;
}

function StatCard({ title, value, sub, icon, iconClass = "dundial-icon-muted" }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
          {title}
        </CardTitle>
        <span className={iconClass} aria-hidden>{icon}</span>
      </CardHeader>
      <CardContent>
        <div className="dundial-stat-value text-3xl">{value}</div>
        {sub && (
          <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground/70">
            {sub}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function CounterCards({ d }: { d: DashboardCounters }) {
  const outcomes = d.exhausted + d.cancelled + d.free_tier_observed;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title="Open"
        value={d.open}
        sub="In active retry"
        icon={<AlertTriangle className="h-4 w-4" />}
        iconClass="dundial-icon-warn"
      />
      <StatCard
        title="Recovered (30d)"
        value={d.recovered}
        sub={`${formatCents(d.recovered_amount_cents)} recovered`}
        icon={<CheckCircle2 className="h-4 w-4" />}
        iconClass="dundial-icon-ok"
      />
      <StatCard
        title="At risk"
        value={formatCents(d.risk_amount_cents)}
        sub="Open invoice total"
        icon={<DollarSign className="h-4 w-4" />}
        iconClass="dundial-icon-risk"
      />
      <StatCard
        title="Outcomes"
        value={outcomes}
        sub={`${d.exhausted} exhausted · ${d.cancelled} cancelled · ${d.free_tier_observed} free-tier`}
        icon={<PieChart className="h-4 w-4" />}
        iconClass="dundial-icon-muted"
      />
    </div>
  );
}

export function DashboardPage() {
  const q = useQuery<DashboardCounters>({
    queryKey: ["dundial", "dashboard"],
    queryFn: fetchDashboard,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="mt-1 font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Per-decline-code retry tuning · 30-day window
        </p>
      </div>

      {q.isLoading && <LoadingState rows={4} />}
      {q.error && <ErrorState error={q.error} retry={() => q.refetch()} />}
      {q.data && <CounterCards d={q.data} />}

      <IntegrationsCard />
      <UpgradeCard />
      <Footer />
    </div>
  );
}
